import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, Container, LinearProgress, Paper, Stack, Typography,
} from '@mui/material';
import maplibregl from 'maplibre-gl';
import { request } from './OperationsProvider';
import { basemapStyle } from './OperationsMap';
import Wordmark from '../shell/Wordmark';
import { sitePin, vehiclePin } from './mapPin';
import { formatDateTime } from '../common/util/datetime';

// What a customer gets when a trip is shared with them: the truck on a map, moving, and nothing
// else about the plant that sent it. The link stops working the moment the trip closes, so the
// tracking ends when the load arrives without anybody remembering to switch it off.
//
// The map opens on satellite imagery, from the same keyless tile source the signed-in map uses. The
// product's own map is Google's, but that needs a key, and this page is read before anybody signs
// in - handing the key to whoever holds a link is not worth a nicer basemap. Imagery was what the
// page was actually missing: a plain street raster is what "it opens a standard map" means.
//
// The truck is drawn with the product's own pin rather than a rectangle built here, so the one
// screen most customers ever see looks like the rest of the system.
const PublicTrackingPage = () => {
  const { token } = useParams();
  const [record, setRecord] = useState(null);
  const [error, setError] = useState(null);
  const host = useRef(null);
  const map = useRef(null);
  const truck = useRef(null);
  const site = useRef(null);
  const fitted = useRef(false);

  useEffect(() => {
    const controller = new AbortController();
    const load = async () => {
      try {
        const data = await request(`/api/public/tracking/${encodeURIComponent(token)}`, 'GET', null, controller.signal);
        setRecord(data); setError(null);
      } catch (err) {
        if (err.name !== 'AbortError') { setRecord(null); setError(err.message); }
      }
    };
    load(); const timer = setInterval(load, 15000);
    return () => { controller.abort(); clearInterval(timer); };
  }, [token]);

  const located = Number.isFinite(Number(record?.position?.latitude));

  // Imagery by default: somebody following a lorry wants to see where it actually is, and a street
  // raster at this zoom is a grid of lines. Switchable, because a street map reads better for a
  // junction or a road name.
  const [satellite, setSatellite] = useState(true);

  useEffect(() => {
    if (!located || !host.current) return undefined;
    const at = { lat: Number(record.position.latitude), lng: Number(record.position.longitude) };
    const target = record.destination;
    // Opened on the truck, at a zoom where the road it is on is readable. Framing it together with
    // a destination hundreds of kilometres away drew the truck as a dot a few pixels wide, which
    // made the one thing the reader came for the hardest thing to find.
    const START = 13;

    if (!map.current) {
      map.current = new maplibregl.Map({
        container: host.current,
        style: basemapStyle(satellite),
        center: [at.lng, at.lat],
        zoom: START,
        attributionControl: true,
      });
      map.current.addControl(new maplibregl.NavigationControl(), 'bottom-right');
    }
    if (!truck.current) {
      truck.current = new maplibregl.Marker({ element: vehiclePin({ label: record.vehicleReference }, true) })
        .setLngLat([at.lng, at.lat]).addTo(map.current);
    } else {
      // Moved, not rebuilt, so the truck slides along rather than blinking every fifteen seconds.
      truck.current.setLngLat([at.lng, at.lat]);
    }
    if (target && !site.current) {
      site.current = new maplibregl.Marker({ element: sitePin({ label: target.name, title: target.name }), anchor: 'bottom' })
        .setLngLat([Number(target.longitude), Number(target.latitude)]).addTo(map.current);
    }
    return undefined;
  }, [record, located]);

  // Swapped in place, so the view and the markers survive the change.
  useEffect(() => { map.current?.setStyle(basemapStyle(satellite)); }, [satellite]);

  // Both at once, when somebody asks for it. A control rather than the opening view, because the
  // truck is what the link was opened for.
  const frameBoth = () => {
    const target = record?.destination;
    if (!map.current || !target || !located) return;
    const at = { lat: Number(record.position.latitude), lng: Number(record.position.longitude) };
    const to = { lat: Number(target.latitude), lng: Number(target.longitude) };
    map.current.fitBounds([[at.lng, at.lat], [to.lng, to.lat]], { padding: 64, duration: 0 });
    fitted.current = true;
  };

  useEffect(() => () => { map.current?.remove(); map.current = null; }, []);

  return (
    <Container maxWidth="sm" sx={{ py: 6 }}>
      <Typography variant="overline">
        <Wordmark />
        {' · SHARED TRACKING'}
      </Typography>
      {error && <Alert severity="info" sx={{ mt: 2 }}>{error}</Alert>}
      {!error && !record && <LinearProgress />}
      {!error && record && (
        <Paper variant="outlined" sx={{ mt: 2, overflow: 'hidden' }}>
          <Box sx={{ p: 3, pb: 2 }}>
            <Stack direction="row" alignItems="center" gap={1.5} flexWrap="wrap">
              <Typography variant="h5">{record.vehicleReference}</Typography>
              <Chip size="small" label={record.journeyStatus.replaceAll('_', ' ').toLowerCase()} sx={{ textTransform: 'capitalize' }} />
            </Stack>
            {record.destination && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {`On its way to ${record.destination.name}`}
              </Typography>
            )}
          </Box>
          {located ? (
            <>
              <Box ref={host} sx={{ height: { xs: 320, sm: 420 }, width: '100%' }} />
              <Stack direction="row" gap={1} flexWrap="wrap" sx={{ px: 3, pt: 1.5 }}>
                <Button size="small" variant="outlined" onClick={() => setSatellite((on) => !on)}>
                  {satellite ? 'Street map' : 'Satellite'}
                </Button>
                {record.destination && (
                  <Button size="small" variant="outlined" onClick={frameBoth}>
                    {`Show ${record.destination.name} as well`}
                  </Button>
                )}
              </Stack>
            </>
          ) : (
            <Box sx={{ px: 3, pb: 2 }}>
              <Typography color="text.secondary">Position is not currently available.</Typography>
            </Box>
          )}
          {located && (
            <Typography variant="caption" color="text.secondary" component="div" sx={{ p: 2 }}>
              {`Last reported ${formatDateTime(record.position.fixTime)} · updates every 15 seconds`}
            </Typography>
          )}
        </Paper>
      )}
    </Container>
  );
};

export default PublicTrackingPage;
